/**
 * TaskKanban - Kanban board view for tasks
 *
 * Displays tasks in columns by status with drag-and-drop support.
 * Can be embedded in cards or opened as a modal.
 *
 * Schema:
 * {
 *   type: 'task-kanban',
 *   tasks: [...],
 *   columns: ['pending', 'in_progress', 'completed'],
 *   allowDrag: true
 * }
 */

import React, { useState, useCallback } from 'react';
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Plus,
  MoreHorizontal,
  X,
} from 'lucide-react';
import './TaskKanban.css';

// Column configuration
const DEFAULT_COLUMNS = [
  { id: 'pending', label: 'To Do', icon: Circle, color: 'var(--color-text-muted)' },
  { id: 'in_progress', label: 'In Progress', icon: Clock, color: 'var(--color-primary)' },
  { id: 'completed', label: 'Done', icon: CheckCircle2, color: 'var(--color-success)' },
];

// Priority colors
const PRIORITY_COLORS = {
  urgent: 'var(--color-error)',
  high: 'var(--color-warning)',
  medium: 'var(--color-primary)',
  low: 'var(--color-text-muted)',
};

/**
 * TaskKanban Component
 */
export default function TaskKanban({
  tasks = [],
  columns = DEFAULT_COLUMNS,
  allowDrag = true,
  compact = false,
  onAction,
  onTaskMove,
  onTaskClick,
  onAddTask,
}) {
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Group tasks by status
  const tasksByColumn = columns.reduce((acc, col) => {
    acc[col.id] = tasks.filter((t) => t.status === col.id);
    return acc;
  }, {});

  // Drag handlers
  const handleDragStart = useCallback((e, task) => {
    if (!allowDrag) return;
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  }, [allowDrag]);

  const handleDragOver = useCallback((e, columnId) => {
    e.preventDefault();
    setDragOverColumn(columnId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback((e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (draggedTask && draggedTask.status !== columnId) {
      if (onTaskMove) {
        onTaskMove(draggedTask.id, columnId);
      }
      if (onAction) {
        onAction({
          type: 'move_task',
          task_id: draggedTask.id,
          task_title: draggedTask.title,
          from_status: draggedTask.status,
          to_status: columnId,
        });
      }
    }

    setDraggedTask(null);
  }, [draggedTask, onTaskMove, onAction]);

  const handleDragEnd = useCallback(() => {
    setDraggedTask(null);
    setDragOverColumn(null);
  }, []);

  // Task click
  const handleTaskClick = useCallback((task) => {
    if (onTaskClick) {
      onTaskClick(task);
    }
    if (onAction) {
      onAction({ type: 'select_task', task_id: task.id, task_title: task.title });
    }
  }, [onTaskClick, onAction]);

  // Add task
  const handleAddTask = useCallback((columnId) => {
    if (onAddTask) {
      onAddTask(columnId);
    }
    if (onAction) {
      onAction({ type: 'add_task', status: columnId });
    }
  }, [onAddTask, onAction]);

  return (
    <div className={`task-kanban ${compact ? 'task-kanban--compact' : ''}`}>
      {columns.map((column) => {
        const Icon = column.icon;
        const columnTasks = tasksByColumn[column.id] || [];
        const isDropTarget = dragOverColumn === column.id;

        return (
          <div
            key={column.id}
            className={`task-kanban__column ${isDropTarget ? 'task-kanban__column--drop-target' : ''}`}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Column header */}
            <div className="task-kanban__column-header">
              <div className="task-kanban__column-title">
                <Icon size={16} style={{ color: column.color }} />
                <span>{column.label}</span>
                <span className="task-kanban__column-count">{columnTasks.length}</span>
              </div>
              <button
                className="task-kanban__add-btn"
                onClick={() => handleAddTask(column.id)}
                aria-label={`Add task to ${column.label}`}
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Tasks */}
            <div className="task-kanban__tasks">
              {columnTasks.map((task) => (
                <KanbanCard
                  key={task.id}
                  task={task}
                  isDragging={draggedTask?.id === task.id}
                  allowDrag={allowDrag}
                  compact={compact}
                  onDragStart={(e) => handleDragStart(e, task)}
                  onDragEnd={handleDragEnd}
                  onClick={() => handleTaskClick(task)}
                />
              ))}

              {columnTasks.length === 0 && (
                <div className="task-kanban__empty">
                  No tasks
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * KanbanCard - Individual task card in kanban
 */
function KanbanCard({
  task,
  isDragging,
  allowDrag,
  compact,
  onDragStart,
  onDragEnd,
  onClick,
}) {
  const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;

  // Check if overdue
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <div
      className={`kanban-card ${isDragging ? 'kanban-card--dragging' : ''} ${isOverdue ? 'kanban-card--overdue' : ''}`}
      style={{ '--priority-color': priorityColor }}
      draggable={allowDrag}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {/* Priority indicator */}
      <div className="kanban-card__priority-bar" />

      {/* Content */}
      <div className="kanban-card__content">
        <div className="kanban-card__title">{task.title}</div>

        <div className="kanban-card__meta">
          {task.account && (
            <span className="kanban-card__account">{task.account}</span>
          )}
          {task.due_date && (
            <span className={`kanban-card__due ${isOverdue ? 'kanban-card__due--overdue' : ''}`}>
              <Clock size={12} />
              {formatDueDate(task.due_date)}
            </span>
          )}
        </div>
      </div>

      {/* Priority badge */}
      {task.priority && task.priority !== 'medium' && (
        <div
          className="kanban-card__priority-badge"
          style={{ background: priorityColor }}
        >
          {task.priority === 'urgent' && <AlertCircle size={10} />}
          {task.priority.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
}

/**
 * Format due date for display
 */
function formatDueDate(dueDate) {
  const due = new Date(dueDate);
  const now = new Date();
  const diffDays = Math.floor((due - now) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `${Math.abs(diffDays)}d ago`;
  }
  if (diffDays === 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return 'Tomorrow';
  }
  if (diffDays <= 7) {
    return `${diffDays}d`;
  }

  return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * TaskKanbanModal - Kanban board in a modal overlay
 */
export function TaskKanbanModal({
  isOpen,
  onClose,
  tasks,
  title = 'Tasks',
  onAction,
  onTaskMove,
}) {
  if (!isOpen) return null;

  return (
    <div className="task-kanban-modal__overlay" onClick={onClose}>
      <div className="task-kanban-modal" onClick={(e) => e.stopPropagation()}>
        <div className="task-kanban-modal__header">
          <h2>{title}</h2>
          <button className="task-kanban-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="task-kanban-modal__body">
          <TaskKanban
            tasks={tasks}
            allowDrag
            onAction={onAction}
            onTaskMove={onTaskMove}
          />
        </div>
      </div>
    </div>
  );
}
